import { useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { toast } from 'sonner';
import { CheckCircle2, Loader2, PackageX } from 'lucide-react';
import api from '../../lib/api';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from '../../components/ui/alert-dialog';
import ScanOrType from './ScanOrType';
import ToolHeader, { NoAccess, Spinner } from './ToolHeader';

const daysLeft = (d, today) => Math.round((new Date(`${d}T00:00:00`) - new Date(`${today}T00:00:00`)) / 86400000);

export const expiryFlag = (days) => {
  if (days < 0) return { label: 'Expired', cls: 'bg-destructive text-white' };
  if (days <= 30) return { label: `${days}d left`, cls: 'bg-destructive/15 text-destructive' };
  if (days <= 90) return { label: `${days}d left`, cls: 'bg-amber-500/15 text-amber-600' };
  return { label: 'OK', cls: 'bg-emerald-500/15 text-emerald-600' };
};

function BatchRow({ productId, productName, b, today, canPull, onPulled }) {
  const f = expiryFlag(daysLeft(b.expiry_date, today));
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const pull = async () => {
    setBusy(true);
    try {
      await api.post('/staff-app/expiry/pull-batch', {
        product_id: productId, batch_number: b.batch_number || '', reason: `${f.label}`,
      });
      toast.success(`Pulled ${b.quantity} unit(s) — awaiting disposal`);
      onPulled(`${productId}|${b.batch_number}|${b.expiry_date}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not pull this batch');
    } finally { setBusy(false); setConfirm(false); }
  };
  return (
    <div className="flex items-center justify-between gap-2 text-xs" data-testid={`staff-app-expiry-batch-${productId}-${b.batch_number || 'nobatch'}`}>
      <span className="text-muted-foreground truncate">{b.batch_number || 'No batch'} · {b.quantity} pcs · exp {b.expiry_date}</span>
      <div className="flex items-center gap-1.5 shrink-0">
        <span className={`font-bold px-1.5 py-0.5 rounded ${f.cls}`}>{f.label}</span>
        {canPull && (
          <>
            <button onClick={() => setConfirm(true)} disabled={busy}
              className="flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-md border border-destructive/40 text-destructive hover:bg-destructive/10 disabled:opacity-50"
              data-testid={`staff-app-expiry-pull-${productId}-${b.batch_number || 'nobatch'}`}>
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <PackageX className="w-3 h-3" />}
              Pull
            </button>
            <AlertDialog open={confirm} onOpenChange={setConfirm}>
              <AlertDialogContent data-testid="staff-app-expiry-pull-dialog">
                <AlertDialogHeader>
                  <AlertDialogTitle>Pull this batch from the shelf?</AlertDialogTitle>
                  <AlertDialogDescription>
                    {productName} — batch {b.batch_number || '—'} · {b.quantity} unit(s). Stock drops to zero
                    and this batch goes to the admin's disposal queue to confirm later.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="staff-app-expiry-pull-cancel">Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={pull} data-testid="staff-app-expiry-pull-confirm">Yes, pull it</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </>
        )}
      </div>
    </div>
  );
}

export default function ExpirySweepPage() {
  const { tools } = useOutletContext();
  const [data, setData] = useState(null);
  const [found, setFound] = useState({});
  const [lastScan, setLastScan] = useState(null);
  const [view, setView] = useState('todo');
  const [choices, setChoices] = useState([]);

  const load = () => api.get('/staff-app/expiry/due').then((r) => setData(r.data)).catch(() => setData({ items: [], today: '' }));
  useEffect(() => { if (tools?.expiry) load(); }, [tools]);

  const list = useMemo(() => (data?.items || []).filter((i) => (view === 'todo' ? !found[i.product_id] : found[i.product_id])), [data, found, view]);
  if (tools && !tools.expiry) return <><ToolHeader title="Expiry sweep" /><NoAccess /></>;
  if (!data) return <><ToolHeader title="Expiry sweep" /><Spinner /></>;

  const show = (p) => {
    setChoices([]);
    const due = data.items.find((i) => i.product_id === p.product_id);
    const batches = (p.batches || []).filter((b) => b.expiry_date && (b.quantity || 0) > 0 && !b.removed_from_shelf && !b.quarantined).map((b) => ({ ...b, expiry_date: b.expiry_date.slice(0, 10) }));
    setLastScan({ product_id: p.product_id, name: p.name, due: !!due, batches });
    if (due) setFound((f) => ({ ...f, [p.product_id]: true }));
    if (navigator.vibrate && due) navigator.vibrate([80, 60, 80]);
  };

  const onCode = async (code) => {
    try {
      const { data: r } = await api.get('/staff-app/products/lookup', { params: { q: code } });
      const ps = r.products;
      const named = ps.filter((x) => x.name.toLowerCase() === code.toLowerCase());
      const dueHits = ps.filter((x) => data.items.some((i) => i.product_id === x.product_id));
      const p = r.exact || ps.length === 1 ? ps[0] : named.length === 1 ? named[0] : dueHits.length === 1 ? dueHits[0] : null;
      if (p) return show(p);
      setLastScan(null);
      setChoices(ps);
      if (!ps.length) toast.error(`No product for "${code}"`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Lookup failed'); }
  };

  const afterPull = async (pulledBatchKey) => {
    await load();
    if (lastScan) {
      setLastScan((prev) => prev ? { ...prev, batches: prev.batches.filter((b) => `${prev.product_id}|${b.batch_number}|${b.expiry_date}` !== pulledBatchKey) } : prev);
    }
  };

  const items = data?.items || [];
  const doneCount = items.filter((i) => found[i.product_id]).length;
  const canPull = !!tools?.expiry_pull;
  return (
    <div data-testid="staff-app-expiry-sweep">
      <ToolHeader title="Expiry sweep" subtitle={`${data.items.length} product(s) expire within 90 days · ${doneCount} found`} />
      <div className="sticky top-[57px] z-10 bg-background/95 backdrop-blur px-4 pt-3 pb-3 space-y-2 border-b border-border">
        <ScanOrType onCode={onCode} continuous testId="staff-app-expiry-scan" />
        {choices.length > 0 && (
          <div className="rounded-xl border border-border bg-card divide-y divide-border max-h-56 overflow-y-auto" data-testid="staff-app-expiry-choices">
            <p className="px-3 py-2 text-xs text-muted-foreground">Several matches — pick one</p>
            {choices.map((c) => (
              <button key={c.product_id} onClick={() => show(c)} className="w-full text-left px-3 py-2 text-sm hover:bg-muted/50" data-testid={`staff-app-expiry-choice-${c.product_id}`}>{c.name}</button>
            ))}
          </div>
        )}
        {lastScan && (
          <div className={`rounded-xl border p-3 space-y-1.5 ${lastScan.due ? 'border-destructive/50 bg-destructive/5' : 'border-emerald-500/40 bg-emerald-500/5'}`} data-testid="staff-app-expiry-last">
            <p className="text-sm font-bold">{lastScan.name} — {lastScan.due ? 'pull or discount soon' : 'no batches due in 90 days'}</p>
            {lastScan.batches.length ? lastScan.batches.map((b) => (
              <BatchRow key={b.batch_number + b.expiry_date} productId={lastScan.product_id} productName={lastScan.name} b={b} today={data.today} canPull={canPull && lastScan.due} onPulled={afterPull} />
            )) : <p className="text-xs text-muted-foreground">No dated batches in stock.</p>}
          </div>
        )}
      </div>
      <div className="p-4 space-y-2.5">
        <div className="flex gap-2">
          {['todo', 'found'].map((v) => (
            <button key={v} onClick={() => setView(v)} data-testid={`staff-app-expiry-view-${v}`}
              className={`flex-1 text-xs font-semibold py-2 rounded-lg border ${view === v ? 'bg-primary text-primary-foreground border-primary' : 'border-border'}`}>
              {v === 'todo' ? `To find (${data.items.length - doneCount})` : `Found (${doneCount})`}
            </button>
          ))}
        </div>
        {list.length === 0 && <p className="py-8 text-center text-sm text-muted-foreground" data-testid="staff-app-expiry-empty">{view === 'todo' ? 'Nothing left to find.' : 'Scan items on the shelf to tick them off.'}</p>}
        {list.map((i) => (
          <div key={i.product_id} className="rounded-xl border border-border bg-card p-3 space-y-1.5" data-testid={`staff-app-expiry-item-${i.product_id}`}>
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-semibold truncate">{i.name}{i.bin_location ? <span className="text-muted-foreground font-normal"> · Bin {i.bin_location}</span> : null}</p>
              {found[i.product_id] && <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />}
            </div>
            {i.batches.map((b) => (
              <BatchRow key={b.batch_number + b.expiry_date} productId={i.product_id} productName={i.name} b={b} today={data.today} canPull={canPull} onPulled={afterPull} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
